import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUserCircle,
  faMoneyBillWave,
  faClock,
  faSignOutAlt,
  faTimes,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/user/Header.css";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("token"));
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Danh sách các liên kết điều hướng
  const navLinks = useMemo(
    () => [
      { path: "/", label: "Trang chủ" },
      { path: "/rooms", label: "Danh sách phòng" },
      { path: "/favourite", label: "Yêu thích" },
      { path: "/about", label: "Giới thiệu" },
      { path: "/contact", label: "Liên hệ" },
    ],
    []
  );

  const isLinkActive = (itemPath) => {
    if (itemPath === "/") {
      return location.pathname === "/";
    }
    if (itemPath === "/rooms") {
      return location.pathname === "/rooms";
    }
    return location.pathname === itemPath;
  };

  // Kiểm tra đăng nhập khi trang được tải lại
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem("token"); // Lấy token từ localStorage
      setIsLoggedIn(!!token);
    };

    checkLoginStatus();
    window.addEventListener("storage", checkLoginStatus);
    return () => window.removeEventListener("storage", checkLoginStatus);
  }, []);

  // Đóng menu khi người dùng click bên ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  

  // Xử lý đăng xuất
  const handleLogout = () => {
    const toastId = toast(
      ({ closeToast }) => (
        <motion.div
          className="logout-toast"
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="toast-header">
            <div className="toast-icon">
              <FontAwesomeIcon icon={faSignOutAlt} />
            </div>
            <h3>Đăng xuất khỏi SPHome?</h3>
          </div>

          <p className="toast-desc">
            Phiên đăng nhập hiện tại sẽ bị kết thúc. Bạn có chắc chắn muốn thoát?
          </p>

          <div className="toast-buttons">
            <button
              className="cancel-btn"
              onClick={() => toast.dismiss(toastId)}
            >
              Hủy
            </button>
            <button
              className="confirm-btn"
              onClick={() => {
                toast.dismiss(toastId);
                performLogout();
              }}
            >
              Đăng xuất
            </button>
          </div>
        </motion.div>
      ),
      {
        position: "top-right",
        autoClose: true,
        hideProgressBar: true,
        closeButton: true,
        pauseOnHover: false,
        closeOnClick: true,
        draggable: false,
        className: "logout-toast-container",
      }
    );
  };

const performLogout = () => {
  // Kiểm tra trạng thái "Lưu thông tin đăng nhập" từ localStorage
  const isRememberMe = localStorage.getItem("rememberMe") === "true";

  // Nếu không chọn "Lưu thông tin đăng nhập", xóa email và mật khẩu
  if (!isRememberMe) {
    localStorage.removeItem("email");
    localStorage.removeItem("password");
  }

  // Xóa toàn bộ thông tin người dùng và token khỏi localStorage
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("rememberMe");  // Xóa trạng thái rememberMe khi đăng xuất

  toast.success("Đã đăng xuất thành công! Hẹn gặp lại bạn 💫", {
    position: "top-right",
    autoClose: 1500,
    className: "logout-success-toast",
    closeOnClick: true,
  });

  setTimeout(() => {
    navigate("/login"); // Chuyển hướng về trang login
    window.location.reload(); // Reload lại trang sau khi chuyển hướng
  }, 1600);
};


  // Xử lý khi click vào logo
  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  return (
    <>
      <header className="nethome-header rr-header">
        <div className="rr-wrap">
          {/* LOGO */}
          <button
            onClick={handleLogoClick}
            className="logo"
            aria-label="SHome"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <LogoImage />
          </button>

          {/* Menu desktop */}
          <nav className="nav">
            {navLinks.map((l) => {
              const isActive = isLinkActive(l.path);
              return (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`nav-item ${isActive ? "is-active" : ""}`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="actions" ref={userMenuRef}>
            {!isLoggedIn ? (
              <Link to="/login" className="btn-login">
                Đăng nhập
              </Link>
            ) : (
              <div className="user-menu-container">
                <button
                  onClick={() => setUserMenuOpen((s) => !s)}
                  className={`btn-avatar ${userMenuOpen ? "active" : ""}`}
                >
                  <FontAwesomeIcon icon={faUser} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      className="user-dropdown"
                      initial={{ opacity: 0, y: -15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Link to="/profile" className="menu-item">
                        <FontAwesomeIcon icon={faUserCircle} /> Thông tin cá nhân
                      </Link>
                      <Link to="/revenue" className="menu-item">
                        <FontAwesomeIcon icon={faMoneyBillWave} /> Doanh thu
                      </Link>
                      <Link to="/history" className="menu-item">
                        <FontAwesomeIcon icon={faClock} /> Lịch sử đặt lịch
                      </Link>
                      <button onClick={handleLogout} className="menu-item logout">
                        <FontAwesomeIcon icon={faSignOutAlt} /> Đăng xuất
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Nút menu mobile */}
            <button
              onClick={() => setMenuOpen((s) => !s)}
              className="btn-menu"
              aria-label="Mở/đóng menu"
            >
              <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
            </button>
          </div>
        </div>
      </header>

      {/* Overlay + Menu trượt ra */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />
            <motion.nav
              className="nav-open"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 20, stiffness: 120 }}
            >
              <div className="nav-header">
                <LogoImage />
                <button
                  onClick={() => setMenuOpen(false)}
                  className="btn-close"
                  aria-label="Đóng menu"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {navLinks.map((l) => {
                const isActive = isLinkActive(l.path);
                return (
                  <Link
                    key={l.path}
                    to={l.path}
                    className={`nav-item ${isActive ? "is-active" : ""}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {l.label}
                  </Link>
                );
              })}
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      <ToastContainer />
    </>
  );
}

// ✅ Logo SHome rõ, lớn, có hiệu ứng nhẹ
function LogoImage() {
  return (
    <img
      src="/icon-logo.png"
      alt="SHome Logo"
      className="logo-img"
      style={{
        height: "80px",
        width: "auto",
        objectFit: "contain",
        borderRadius: "14px",
        transition: "all 0.3s ease",
        filter: "none !important",
      }}
      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
    />
  );
}
